import type { ISnapshotData } from '../types';

import { DEFAULT_VIEW_ID } from '@gem-sdk/heatmap-react';
import { create } from 'zustand';

interface IHeatmapSnapshotState {
  openModal: Map<string, boolean>;
  previewSnapshot: Map<string, ISnapshotData | undefined>;
  /** Snapshot list per viewId */
  data: Map<string, ISnapshotData[]>;
  /** Loading state per viewId */
  loading: Map<string, boolean>;
  /** Selected snapshot per viewId */
  selectedSnapshot: Map<string, ISnapshotData | undefined>;
}

interface IHeatmapSnapshotActions {
  setOpenModal: (openModal: boolean, viewId?: string) => void;
  setPreviewSnapshot: (snapshot: ISnapshotData | undefined, viewId?: string) => void;
  setData: (data: ISnapshotData[], viewId?: string) => void;
  setLoading: (isLoading: boolean, viewId?: string) => void;
  setSelectedSnapshot: (snapshot?: ISnapshotData, viewId?: string) => void;
  copyView: (fromViewId: string, toViewId: string) => void;
  clearView: (viewId: string) => void;
  resetAll: () => void;
}

export interface IHeatmapSnapshotStore extends IHeatmapSnapshotState, IHeatmapSnapshotActions {}

export const useHeatmapSnapshotStore = create<IHeatmapSnapshotStore>()((set, get) => ({
  openModal: new Map(),
  previewSnapshot: new Map(),
  data: new Map(),
  loading: new Map(),
  selectedSnapshot: new Map(),

  setOpenModal: (openModal, viewId = DEFAULT_VIEW_ID) => {
    const newOpenModal = new Map(get().openModal);
    newOpenModal.set(viewId, openModal);
    set({ openModal: newOpenModal });
  },

  setPreviewSnapshot: (previewSnapshot, viewId = DEFAULT_VIEW_ID) => {
    const newPreviewSnapshot = new Map(get().previewSnapshot);
    newPreviewSnapshot.set(viewId, previewSnapshot);
    set({ previewSnapshot: newPreviewSnapshot });
  },

  setData: (data, viewId = DEFAULT_VIEW_ID) => {
    const newData = new Map(get().data);
    newData.set(viewId, data);
    set({ data: newData });
  },

  setLoading: (isLoading, viewId = DEFAULT_VIEW_ID) => {
    const newLoading = new Map(get().loading);
    newLoading.set(viewId, isLoading);
    set({ loading: newLoading });
  },

  setSelectedSnapshot: (snapshot, viewId = DEFAULT_VIEW_ID) => {
    const newSelected = new Map(get().selectedSnapshot);
    newSelected.set(viewId, snapshot);
    set({ selectedSnapshot: newSelected });
  },

  copyView: (fromViewId, toViewId) =>
    set((prev) => {
      const newData = new Map(prev.data);
      const newLoading = new Map(prev.loading);
      const newSelected = new Map(prev.selectedSnapshot);
      const newPreviewSnapshot = new Map(prev.previewSnapshot);
      const newOpenModal = new Map(prev.openModal);

      newData.set(toViewId, prev.data.get(fromViewId) ?? []);
      newLoading.set(toViewId, prev.loading.get(fromViewId) ?? false);
      newSelected.set(toViewId, prev.selectedSnapshot.get(fromViewId));
      newPreviewSnapshot.set(toViewId, prev.previewSnapshot.get(fromViewId));
      newOpenModal.set(toViewId, prev.openModal.get(fromViewId) ?? false);

      return {
        data: newData,
        loading: newLoading,
        selectedSnapshot: newSelected,
        previewSnapshot: newPreviewSnapshot,
        openModal: newOpenModal,
      };
    }),

  clearView: (viewId) =>
    set((prev) => {
      const newData = new Map(prev.data);
      const newLoading = new Map(prev.loading);
      const newSelected = new Map(prev.selectedSnapshot);
      const newPreviewSnapshot = new Map(prev.previewSnapshot);
      const newOpenModal = new Map(prev.openModal);

      newData.delete(viewId);
      newLoading.delete(viewId);
      newSelected.delete(viewId);
      newPreviewSnapshot.delete(viewId);
      newOpenModal.delete(viewId);

      return {
        data: newData,
        loading: newLoading,
        selectedSnapshot: newSelected,
        previewSnapshot: newPreviewSnapshot,
        openModal: newOpenModal,
      };
    }),

  resetAll: () =>
    set({
      openModal: new Map(),
      data: new Map(),
      previewSnapshot: new Map(),
      loading: new Map(),
      selectedSnapshot: new Map(),
    }),
}));
